/* eslint-disable react/display-name */
import {
  Button,
  Drawer,
  Grid,
  makeStyles,
  Paper,
  TablePagination,
  Typography,
} from "@material-ui/core";
import {
  AddCircleRounded,
  CalendarToday,
  CheckCircleRounded,
  Edit,
  FilterList,
  Search,
  Send,
} from "@material-ui/icons";
import { format, parse } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { useMeetList } from "../../../context/lists/meetList";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import { getAll } from "../../../services/meeting.service";
import {
  getDateFormats,
  getDateTimeLocale,
  getTimeFormats,
} from "../../../utils/get-date-time-locale";
import SendInviteModal from "../../Common/SendInviteModal";
import { Table } from "../../Common/Table";
import ToastMessage from "../../Common/ToastMessage";
import { getMeetStatus } from "../utils";
import { checkAccess } from "../utils/index";
import ModalBase from "../utils/ModalBase";
import BulkUpdateMeetingsModal from "./BulkUpdateMeetingsModal";
import FilterDrawer from "./FilterDrawer";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
    width: "100%",
  },
  fullWidthContainer: {
    maxWidth: "100%",
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh",
  },
  filterButton: {
    ...theme.typography.subtitle1,
    backgroundColor: "#fff",
    color: "#666666",
    borderRadius: 8,
    border: "1px solid #666666",
    textTransform: "uppercase",
    maxWidth: 140,
    width: "100%",
  },
  spacer: {
    marginBottom: 20,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50,
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText,
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50,
  },
  titleModal: {
    color: "#031795",
    fontWeight: "bold",
    fontSize: "20px",
  },
}));

const ListMeetings = ({ t, history, actionShowSpinner, location }) => {
  const classes = useStyles();
  const { userAccess } = useAuth();
  const { i18n } = useTranslation();
  const { listOptions, updatePagination, updateFilterParams } = useMeetList();

  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  const [successCancelMeet, setSuccessCancelMeet] = useState(
    location?.state?.successCancelMeet
  );
  const [successCreateMeet, setSuccessCreateMeet] = useState(
    location?.state?.successCreateMeet
  );
  const [successCreateSchedule, setSuccesCreateSchedule] = useState(
    location?.state?.successCreateSchedule
  );
  const [successUpdateState, setSuccessUpdateState] = useState(
    location?.state?.successUpdateMeet
  );
  const [successCancelMeetState, setSuccessCancelMeetState] = useState(
    location?.state?.successCancelMeeting
  );
  const [successDeleteMeetState, setSuccessDeleteMeetState] = useState(
    location?.state?.successDeleteMeet
  );
  const [sendInviteSuccess, setSendInviteSuccess] = useState(false);

  const [openModalSendEmail, setModalSendEmail] = useState(false);
  const [meetingToSend, setMeetingToSend] = useState({});
  const [toastMessage, setToastMessage] = useState({
    message: "",
    type: "error",
    open: false,
  });

  const [totalMeets, setTotalMeets] = useState(0);

  const [canCreateMeet, setCanCreateMeet] = useState(false);
  const [canUpdateMeet, setCanUpdateMeet] = useState(false);
  const [canShowSchedule, setCanShowSchedule] = useState(false);
  const [canSendInviteMeet, setCanSendInviteMeet] = useState(false);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [canBulkUpdate, setCanBulkUpdate] = useState(false);

  const [formattedMeets, setFormattedMeets] = useState([]);

  const getMeets = useCallback(
    async ({ filters, listSize, page }) => {
      actionShowSpinner(true);

      try {
        const {
          data: meets,
          countItems,
          error,
        } = await getAll({
          pageSize: listSize,
          currentPage: page,
          filters: {
            ...(filters?.committee && { committeeId: filters.committee.id }),
            ...(filters?.status && { status: filters.status.map((s) => s.id) }),
            ...(filters?.startDate && { startDate: filters.startDate }),
            ...(filters?.endDate && { endDate: filters.endDate }),
            ...(filters?.keyword?.length > 0 && { keyword: filters.keyword }),
            ...(filters?.roundRobin && { roundRobin: filters.roundRobin.id }),
          },
        });

        if (error.length > 0) {
          return;
        }

        setTotalMeets(countItems);

        const newMeets = meets?.map((meet) => {
          return {
            ...meet,
            committeName: (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingRight: "18px",
                }}
              >
                {meet.committeName}
                {meet.roundRobin && (
                  <div
                    style={{
                      marginLeft: "6px",
                      background: "#031795",
                      width: "20px",
                      padding: "12px",
                      color: "#fff",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      fontSize: "12px",
                    }}
                  >
                    RR
                  </div>
                )}
              </div>
            ),
            statusName: meet.statusName,
            meetStatusCode: meet.statusName,
            meetDate: new Date(
              meet.meetDate.split("/")[2],
              meet.meetDate.split("/")[1] - 1,
              meet.meetDate.split("/")[0]
            ),
          };
        });

        setFormattedMeets(newMeets);
      } catch (err) {
        setToastMessage({
          message:
            "Ocorreu um erro ao listar as reuniões, entre em contato com o suporte.",
          type: "error",
          open: true,
        });
      } finally {
        actionShowSpinner(false);
      }
    },
    [actionShowSpinner]
  );

  useEffect(() => {
    if (
      typeof location?.state?.successCancelMeet === "boolean" &&
      location?.state?.successCancelMeet
    ) {
      setTimeout(() => setSuccessCancelMeet(false), 10000);
    }

    if (
      typeof location?.state?.successCreateMeet === "boolean" &&
      location?.state?.successCreateMeet
    ) {
      setTimeout(() => setSuccessCreateMeet(false), 10000);
    }

    if (
      typeof location?.state?.successUpdateMeet === "boolean" &&
      location?.state?.successUpdateMeet
    ) {
      setTimeout(() => setSuccessUpdateState(false), 10000);
    }

    if (
      typeof location?.state?.successCreateSchedule === "boolean" &&
      location?.state?.successCreateSchedule
    ) {
      setTimeout(() => setSuccesCreateSchedule(false), 10000);
    }

    if (
      typeof location?.state?.successCancelMeeting === "boolean" &&
      location?.state?.successCancelMeeting
    ) {
      setTimeout(() => setSuccessCancelMeetState(false), 10000);
    }

    if (
      typeof location?.state?.successDeleteMeet === "boolean" &&
      location?.state?.successDeleteMeet
    ) {
      setTimeout(() => setSuccessDeleteMeetState(false), 10000);
    }
  }, [successUpdateState, location]);

  useEffect(() => {
    if (userAccess) {
      const hasAccess = checkAccess(userAccess, "listMeets");
      const hasAccessToCreateMeet = checkAccess(userAccess, "createMeet");
      const hasAccessToUpdateMeet = checkAccess(userAccess, "updateMeet");
      const hasAccessToShowSchedule = checkAccess(
        userAccess,
        "showScheduleMeet"
      );
      const hasAceessToSendInviteMeet = checkAccess(
        userAccess,
        "sendInviteMeet"
      );

      setCanCreateMeet(hasAccessToCreateMeet);
      setCanUpdateMeet(hasAccessToUpdateMeet);
      setCanShowSchedule(hasAccessToShowSchedule);
      setCanSendInviteMeet(hasAceessToSendInviteMeet);
      setCanBulkUpdate(checkAccess(userAccess, "bulkUpdateMeetings"));

      if (!hasAccess) {
        history.push("/");
      }
    }
  }, [history, userAccess]);

  useEffect(() => {
    getMeets({
      filters: listOptions.filterParams,
      page: listOptions.pagination.page,
      listSize: listOptions.pagination.size,
    });
  }, [listOptions, getMeets, i18n.language]);

  function formatTimePeriodString(startTime, endTime, timeFormat) {
    const parsedStartTime = parse(startTime, "HH:mm", new Date());
    const parsedEndTime = parse(endTime, "HH:mm", new Date());

    const formattedStartTime = format(parsedStartTime, timeFormat);
    const formattedEndTime = format(parsedEndTime, timeFormat);

    return `${formattedStartTime} ${t(
      "timePeriodSeparator"
    )} ${formattedEndTime}`;
  }

  const columns = [
    {
      title: t("forms.committeeName").toUpperCase(),
      field: "committeName",
    },
    {
      title: t("meeting.meetingDate").toUpperCase(),
      field: "meetDate",
      render: (row) => {
        return format(new Date(row.meetDate), getDateFormats(i18n.language), {
          locale: getDateTimeLocale(i18n.language),
        });
      },
    },
    {
      title: t("meeting.time").toUpperCase(),
      field: "meetStartTime",
      render: (row) => {
        return formatTimePeriodString(
          row.meetStartTime,
          row.meetEndTime,
          getTimeFormats(i18n.language)
        );
      },
    },
    {
      title: t("forms.committee.secretary").toUpperCase(),
      field: "secretaryName",
    },
    {
      title: t("status").toUpperCase(),
      field: "statusName",
      render: (row) => getMeetStatus(row.statusName),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <ToastMessage
        message={toastMessage.message}
        type={toastMessage.type || "error"}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
      <ModalBase
        open={openModalSendEmail}
        setOpen={setModalSendEmail}
        content={
          <SendInviteModal
            t={t}
            setModalSendEmail={(newValue) => setModalSendEmail(newValue)}
            meetingInfo={meetingToSend}
            onSuccess={() => {
              setModalSendEmail(false);
              setSendInviteSuccess(true);

              getMeets({
                filters: listOptions.filterParams,
                page: listOptions.pagination.page,
                listSize: listOptions.pagination.size,
              });

              setTimeout(() => setSendInviteSuccess(false), 4000);
            }}
          />
        }
      ></ModalBase>
      <Drawer
        anchor="right"
        open={filterDrawerOpened}
        onClose={() => {
          setFilterDrawerOpened(false);
        }}
      >
        <FilterDrawer
          actionShowSpinner={(opened) => actionShowSpinner(opened)}
          onClose={() => {
            setFilterDrawerOpened(false);
          }}
          t={t}
          setFilters={(newValues) => {
            updateFilterParams(newValues);
            updatePagination({ ...listOptions.pagination, page: 0 });
          }}
          filters={listOptions.filterParams}
        />
      </Drawer>
      <Grid item className={classes.spacer}>
        <Typography variant="h4">{t("meeting.title")}</Typography>
      </Grid>
      <Grid container lg={12}>
        <Paper elevation={1} className={classes.root}>
          <Grid container spacing={3} className={classes.fullWidthContainer}>
            <Grid item lg={3} className={classes.fullWidthContainer}>
              <Button
                className={classes.filterButton}
                startIcon={<FilterList style={{ fontSize: 30 }} />}
                onClick={() => setFilterDrawerOpened(true)}
              >
                {t("forms.filters")}
              </Button>
            </Grid>

            <Grid item lg={12} className={classes.fullWidthContainer}>
              <Table
                columns={columns}
                data={formattedMeets}
                actions={[
                  (rowData) => ({
                    icon: () => <Search />,
                    tooltip: t("preview"),
                    onClick: (_event, { id, statusName }) => {
                      history.push({
                        pathname: `/meeting/detail/${id}`,
                        state: {
                          detailMeet: true,
                        },
                      });
                    },
                  }),
                  (rowData) => ({
                    icon: () => <Edit />,
                    tooltip: t("edit"),
                    disabled:
                      !canUpdateMeet ||
                      rowData.meetStatusCode === "C" ||
                      rowData.meetStatusCode === "UD" ||
                      rowData.meetStatusCode === "CC",
                    onClick: (_event, { id, statusName }) => {
                      history.push(`/meeting/edit/${id}`);
                    },
                  }),

                  (rowData) => ({
                    icon: () => <Send />,
                    tooltip: t("meeting.sendInvite"),
                    disabled:
                      !canSendInviteMeet ||
                      rowData.meetStatusCode === "C" ||
                      rowData.meetStatusCode === "CC" ||
                      rowData.roundRobin,
                    onClick: async (_event, meetingInfo) => {
                      setModalSendEmail(!openModalSendEmail);

                      setMeetingToSend(meetingInfo);
                    },
                  }),
                  (rowData) => ({
                    icon: () => <CalendarToday />,
                    tooltip: t("meeting.meetingSchedule"),
                    disabled:
                      !canShowSchedule || rowData.meetStatusCode === "C",
                    onClick: (_event, { id, meetStatusCode, statusName }) => {
                      history.push(`/meeting/info/${id}`);
                    },
                  }),
                ]}
                pageSize={listOptions.pagination.size}
                components={{
                  Pagination: (props) => {
                    return (
                      <TablePagination
                        style={{ float: "right" }}
                        rowsPerPageOptions={[5, 10, 20]}
                        rowsPerPage={listOptions.pagination.size}
                        labelRowsPerPage={t("table.labelRowsPerPage")}
                        page={listOptions.pagination.page}
                        labelDisplayedRows={(_data) => {
                          let numberOfPages = Math.ceil(
                            totalMeets / listOptions.pagination.size
                          );

                          if (numberOfPages <= 1 || isNaN(numberOfPages)) {
                            numberOfPages = 1;
                          }

                          return `${listOptions.pagination.page + 1} ${t(
                            "table.labelDisplayedRows"
                          )} ${numberOfPages}`;
                        }}
                        onPageChange={(_e, page) => {
                          updatePagination({
                            page: page,
                            size: listOptions.pagination.size,
                          });
                        }}
                        onRowsPerPageChange={(event) => {
                          const newSize = event.target.value;

                          updatePagination({
                            page: 0,
                            size: newSize,
                          });

                          props.onChangeRowsPerPage(event);
                        }}
                        nextIconButtonProps={{
                          disabled:
                            listOptions.pagination.page + 1 >=
                            Math.ceil(totalMeets / listOptions.pagination.size),
                        }}
                        backIconButtonProps={{
                          disabled: listOptions.pagination.page <= 0,
                        }}
                      />
                    );
                  },
                }}
              />
            </Grid>

            <Grid item lg={12}>
              {successCreateSchedule && (
                <Grid
                  style={{
                    width: "100%",
                  }}
                  container
                  item
                  direction="row"
                  alignItems="center"
                  justify="center"
                  className={classes.success}
                >
                  <Grid item>
                    <CheckCircleRounded className={classes.icon} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("meeting.confirmedAgendaSuccess")}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {successCancelMeetState && (
                <Grid
                  style={{
                    width: "100%",
                  }}
                  container
                  item
                  direction="row"
                  alignItems="center"
                  justify="center"
                  className={classes.success}
                >
                  <Grid item>
                    <CheckCircleRounded className={classes.icon} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("meeting.cancelMeetSuccess")}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {successDeleteMeetState && (
                <Grid
                  style={{
                    width: "100%",
                  }}
                  container
                  item
                  direction="row"
                  alignItems="center"
                  justify="center"
                  className={classes.success}
                >
                  <Grid item>
                    <CheckCircleRounded className={classes.icon} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("meeting.deleteMeetSuccess")}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {successUpdateState && (
                <Grid
                  style={{
                    width: "100%",
                  }}
                  container
                  item
                  direction="row"
                  alignItems="center"
                  justify="center"
                  className={classes.success}
                >
                  <Grid item>
                    <CheckCircleRounded className={classes.icon} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("meeting.updatedMeetSuccess")}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {successCreateMeet && (
                <Grid
                  container
                  item
                  direction="row"
                  alignItems="center"
                  justify="center"
                  className={classes.success}
                >
                  <Grid item>
                    <CheckCircleRounded className={classes.icon} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("meeting.createMeetSuccess")}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {successCancelMeet && (
                <Grid
                  container
                  item
                  direction="row"
                  alignItems="center"
                  justify="center"
                  className={classes.success}
                >
                  <Grid item>
                    <CheckCircleRounded className={classes.icon} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("meeting.cancelAgenda")}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {sendInviteSuccess && (
                <Grid
                  container
                  style={{
                    width: "100%",
                  }}
                  item
                  direction="row"
                  alignItems="center"
                  justify="center"
                  className={classes.success}
                >
                  <Grid item>
                    <CheckCircleRounded className={classes.icon} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("meeting.invitationSuccess")}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid container direction="row" justify="center" alignItems="center" spacing={2}>
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            startIcon={<AddCircleRounded style={{ fontSize: 30 }} />}
            onClick={() => {
              history.push("/meeting/new");
            }}
            disabled={!canCreateMeet}
          >
            {t("meeting.addMeetingTable")}
          </Button>
        </Grid>
        {canBulkUpdate && (
          <Grid item>
            <Button
              className={classes.button}
              variant="contained"
              startIcon={<Edit style={{ fontSize: 30 }} />}
              onClick={() => setBulkUpdateModalOpen(true)}
            >
              {t("meeting.bulkUpdate.buttonLabel", "Atualização em Massa")}
            </Button>
          </Grid>
        )}
      </Grid>
      <ModalBase
        open={bulkUpdateModalOpen}
        setOpen={setBulkUpdateModalOpen}
        content={
          <BulkUpdateMeetingsModal
            onClose={() => setBulkUpdateModalOpen(false)}
            onSuccess={(toast) => {
              setBulkUpdateModalOpen(false);
              setToastMessage({
                open: true,
                content: toast.message,
                type: toast.type,
                autoHideDuration: 4000,
              });
              getMeets({
                filters: listOptions.filterParams,
                page: listOptions.pagination.page,
                listSize: listOptions.pagination.size,
              });
            }}
          />
        }
      />
    </Grid>
  );
};

const withRouterListMeetings = withRouter(ListMeetings);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const ListMeetingsConnect = connect(
  null,
  mapDispatchToProps
)(withRouterListMeetings);

export default withTranslation()(ListMeetingsConnect);
